import { Component, type ErrorInfo, type ReactNode } from 'react';

import { Button, Label } from './primitives';
import './ErrorBoundary.css';

interface Props {
  /** Named so the message can say which part of the app failed. */
  area: string;
  children: ReactNode;
}

interface State {
  failed: boolean;
}

/**
 * A route that throws shows a recoverable message, never a blank page and never
 * a stack trace. The exception text is deliberately not rendered: it is written
 * to the console for whoever is debugging, and the user gets a sentence they can
 * act on instead.
 */
export default class ErrorBoundary extends Component<Props, State> {
  state: State = { failed: false };

  static getDerivedStateFromError(): State {
    return { failed: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // The console is the right place for this. The interface is not.
    console.error(`[${this.props.area}] render failed`, error, info.componentStack);
  }

  private reset = () => {
    this.setState({ failed: false });
  };

  render(): ReactNode {
    if (!this.state.failed) return this.props.children;

    return (
      <div className="eb">
        <Label tone="amber">This page stopped working</Label>
        <h1 className="eb__head">Something in {this.props.area} failed to render.</h1>
        <p className="eb__body">
          Nothing has been lost. Documents and review marks are stored in this
          browser and are unaffected by a rendering fault. The details have been
          written to the browser console for whoever maintains this.
        </p>
        <p className="eb__body">
          Try this page again. If it fails a second time, the review of the
          worked example is bundled with the app and does not depend on anything
          stored here.
        </p>
        <div className="eb__actions">
          <Button variant="primary" marker onClick={this.reset}>
            Try this page again
          </Button>
          <Button variant="secondary" marker to="/review/piscataway">
            Open the example review
          </Button>
        </div>
      </div>
    );
  }
}
